import {
  type PointerEvent as ReactPointerEvent,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { type WorkspaceAgentPanelFloatRect } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentPanelFloatRect';
import { type WorkspaceAgentPanelResizeEdges } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentPanelResizeEdges';
import { clampWorkspaceAgentPanelFloatRect } from '~/components/v2/components/workspace/workspace-agent-panel/utils/clampWorkspaceAgentPanelFloatRect';
import { getWorkspaceAgentPanelDropDock } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentPanelDropDock';
import { getWorkspaceAgentPanelResizedFloatRect } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentPanelResizedFloatRect';
import { WORKSPACE_AGENT_PANEL_DRAG_THRESHOLD_PX } from '~/components/v2/components/workspace/workspace-agent-panel/workspaceAgentPanelConstants';
import {
  useWorkspaceAgentPanelStore,
  type WorkspaceAgentDock,
} from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';

const FLOAT_DRAG_IGNORE_SELECTOR = 'button, a, input, textarea, [role="menu"], [data-workspace-agent-panel-resize]';

export type { WorkspaceAgentPanelResizeEdges };

type WorkspaceAgentPanelPointerSession = {
  hasDetached: boolean;
  offsetX: number;
  offsetY: number;
  pointerId: number;
  resize: WorkspaceAgentPanelResizeEdges | null;
  sourceDock: WorkspaceAgentDock;
  startHeight: number;
  startLeft: number;
  startTop: number;
  startWidth: number;
  startX: number;
  startY: number;
};

export type UseWorkspaceAgentPanelFloatInteractionParams = {
  panelRef: RefObject<HTMLElement>;
};

export type UseWorkspaceAgentPanelFloatInteractionResult = {
  isDragging: boolean;
  onHeaderPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
  onResizePointerDown: (edges: WorkspaceAgentPanelResizeEdges) => (event: ReactPointerEvent<HTMLElement>) => void;
  /** True while a header drag preview is inside the right-edge snap zone (not during resize). */
  showSnapToRightGuide: boolean;
};

const applyPanelFloatInlinePreview = (panel: HTMLElement, rect: WorkspaceAgentPanelFloatRect) => {
  // Drop docked/asset-view anchors so a fixed panel cannot stretch between left and right.
  panel.style.bottom = 'auto';
  panel.style.right = 'auto';
  panel.style.height = `${rect.height}px`;
  panel.style.left = `${rect.left}px`;
  panel.style.maxWidth = `${rect.width}px`;
  panel.style.top = `${rect.top}px`;
  panel.style.width = `${rect.width}px`;
};

const getViewportSize = () => ({
  viewportHeight: window.innerHeight,
  viewportWidth: window.innerWidth,
});

const isIgnoredFloatPointerTarget = (target: EventTarget | null) =>
  target instanceof Element && target.closest(FLOAT_DRAG_IGNORE_SELECTOR) !== null;

/**
 * Pointer-driven undock, move, resize, and snap-to-dock for the workspace agent panel.
 *
 * Wired from `PrivateWorkspaceAgentPanelRoot`: the header grip calls `onHeaderPointerDown`;
 * `WorkspaceAgentPanelFloatResizeHandles` call `onResizePointerDown` (float only). Final layout
 * still comes from `useWorkspaceAgentPanelViewport` via the `style` prop — this hook only drives
 * gestures and writes geometry into `useWorkspaceAgentPanelStore` on release.
 *
 * Each gesture is a window-level pointer session (`pointermove` / `pointerup` on `window`, one
 * `pointerId`). Resize sessions carry which edges are active; header sessions use `resize: null`.
 *
 * **Header drag (undock + move)** — Ignores presses on controls matching
 * `FLOAT_DRAG_IGNORE_SELECTOR`. Until the pointer moves past `WORKSPACE_AGENT_PANEL_DRAG_THRESHOLD_PX`,
 * nothing happens (so a click on the grip does not undock). After that, if the panel was docked
 * right, `float(nextRect)` runs once so the store and spacer match a detached card; further moves
 * only update the preview. Undock from the in-flow slot uses stored `floatWidth` / `floatHeight`,
 * not the live spacer box.
 *
 * **Resize** — Same session machinery with edge/corner flags. Left/top handles adjust `left`/`top`
 * as well as width/height so the opposite edge stays anchored.
 *
 * **Preview vs commit** — On every move, `showPreviewRect` writes the clamped rect onto the panel
 * node with inline styles (`applyPanelFloatInlinePreview`), including `right`/`bottom: auto` so a
 * fixed card cannot stretch between dock anchors. That avoids `setFloatGeometry` (and heavy React
 * work) on each `pointermove`; `isDragging` flips true once per session for header chrome. On
 * `pointerup`, `commitRect` clamps again, then either `setFloatGeometry` or `dockToRight` when a
 * **drag** (not resize) ends within snap range of the viewport's right edge
 * (`getWorkspaceAgentPanelDropDock`). While in that zone during a header drag, `showSnapToRightGuide`
 * toggles a viewport-fixed line at the right edge. Inline preview styles are left in place; the next
 * React `style` from the viewport hook subsumes them after the store updates.
 */
export const useWorkspaceAgentPanelFloatInteraction = ({
  panelRef,
}: UseWorkspaceAgentPanelFloatInteractionParams): UseWorkspaceAgentPanelFloatInteractionResult => {
  const dock = useWorkspaceAgentPanelStore((state) => state.dock);
  const floatHeight = useWorkspaceAgentPanelStore((state) => state.floatHeight);
  const floatWidth = useWorkspaceAgentPanelStore((state) => state.floatWidth);
  const float = useWorkspaceAgentPanelStore((state) => state.float);
  const setFloatGeometry = useWorkspaceAgentPanelStore((state) => state.setFloatGeometry);
  const dockToRight = useWorkspaceAgentPanelStore((state) => state.dockToRight);
  const sessionRef = useRef<WorkspaceAgentPanelPointerSession>();
  const previewRectRef = useRef<WorkspaceAgentPanelFloatRect>();
  const isDraggingRef = useRef<boolean>();
  const snapToRightGuideRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showSnapToRightGuide, setShowSnapToRightGuide] = useState(false);

  const syncSnapToRightGuide = useCallback(
    ({
      rect,
      resize,
    }: {
      rect: WorkspaceAgentPanelFloatRect | undefined;
      resize: WorkspaceAgentPanelResizeEdges | null;
    }) => {
      const willSnap =
        rect !== undefined &&
        resize === null &&
        getWorkspaceAgentPanelDropDock({
          left: rect.left,
          viewportWidth: getViewportSize().viewportWidth,
          width: rect.width,
        }) === 'right';

      if (willSnap === snapToRightGuideRef.current) {
        return;
      }

      snapToRightGuideRef.current = willSnap;
      setShowSnapToRightGuide(willSnap);
    },
    [],
  );

  const showPreviewRect = useCallback(
    (rect: WorkspaceAgentPanelFloatRect | undefined, resize: WorkspaceAgentPanelResizeEdges | null = null) => {
      previewRectRef.current = rect;

      if (rect === undefined) {
        isDraggingRef.current = false;
        setIsDragging(false);
        syncSnapToRightGuide({ rect: undefined, resize: null });

        return;
      }

      const panel = panelRef.current;

      if (panel) {
        applyPanelFloatInlinePreview(panel, rect);
      }

      syncSnapToRightGuide({ rect, resize });

      if (!isDraggingRef.current) {
        isDraggingRef.current = true;
        setIsDragging(true);
      }
    },
    [panelRef, syncSnapToRightGuide],
  );

  const commitRect = useCallback(
    ({ rect, snapToRight }: { rect: WorkspaceAgentPanelFloatRect; snapToRight: boolean }) => {
      const { viewportHeight, viewportWidth } = getViewportSize();
      const nextRect = clampWorkspaceAgentPanelFloatRect({ ...rect, viewportHeight, viewportWidth });
      const dropDock = snapToRight
        ? getWorkspaceAgentPanelDropDock({
            left: nextRect.left,
            viewportWidth,
            width: nextRect.width,
          })
        : 'float';

      if (dropDock === 'right') {
        dockToRight();
        showPreviewRect(undefined);

        return;
      }

      setFloatGeometry(nextRect);
      showPreviewRect(undefined);
    },
    [dockToRight, setFloatGeometry, showPreviewRect],
  );

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const session = sessionRef.current;

      if (!session) {
        return;
      }

      const { viewportHeight, viewportWidth } = getViewportSize();

      if (session.resize) {
        showPreviewRect(
          getWorkspaceAgentPanelResizedFloatRect({
            deltaX: event.clientX - session.startX,
            deltaY: event.clientY - session.startY,
            resize: session.resize,
            startHeight: session.startHeight,
            startLeft: session.startLeft,
            startTop: session.startTop,
            startWidth: session.startWidth,
            viewportHeight,
            viewportWidth,
          }),
          session.resize,
        );

        return;
      }

      if (!session.hasDetached) {
        const travel = Math.hypot(event.clientX - session.startX, event.clientY - session.startY);

        if (travel < WORKSPACE_AGENT_PANEL_DRAG_THRESHOLD_PX) {
          return;
        }

        session.hasDetached = true;
      }

      const nextRect = clampWorkspaceAgentPanelFloatRect({
        height: session.startHeight,
        left: event.clientX - session.offsetX,
        top: event.clientY - session.offsetY,
        viewportHeight,
        viewportWidth,
        width: session.startWidth,
      });

      if (session.sourceDock !== 'float') {
        float(nextRect);
        session.sourceDock = 'float';
        session.startHeight = nextRect.height;
        session.startWidth = nextRect.width;
      }

      showPreviewRect(nextRect, null);
    },
    [float, showPreviewRect],
  );

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      const session = sessionRef.current;

      if (!session || event.pointerId !== session.pointerId) {
        return;
      }

      sessionRef.current = undefined;
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      document.body.style.removeProperty('user-select');

      if (!session.hasDetached && !session.resize) {
        showPreviewRect(undefined);

        return;
      }

      const current = previewRectRef.current;

      if (!current) {
        showPreviewRect(undefined);

        return;
      }

      commitRect({ rect: current, snapToRight: session.resize === null });
    },
    [commitRect, handlePointerMove, showPreviewRect],
  );

  const beginSession = useCallback(
    ({ event, resize }: { event: ReactPointerEvent<HTMLElement>; resize: WorkspaceAgentPanelResizeEdges | null }) => {
      const panel = panelRef.current;

      if (!panel || event.button !== 0 || sessionRef.current) {
        return;
      }

      const rect = panel.getBoundingClientRect();
      const isFloating = dock === 'float';
      const isResize = resize !== null;
      const startHeight = isFloating || isResize ? rect.height : floatHeight;
      const startWidth = isFloating || isResize ? rect.width : floatWidth;

      sessionRef.current = {
        hasDetached: isFloating,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        pointerId: event.pointerId,
        resize,
        sourceDock: dock,
        startHeight,
        startLeft: rect.left,
        startTop: rect.top,
        startWidth,
        startX: event.clientX,
        startY: event.clientY,
      };

      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
      window.addEventListener('pointercancel', handlePointerUp);
    },
    [dock, floatHeight, floatWidth, handlePointerMove, handlePointerUp, panelRef],
  );

  const onHeaderPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (isIgnoredFloatPointerTarget(event.target)) {
        return;
      }

      beginSession({ event, resize: null });
    },
    [beginSession],
  );

  const onResizePointerDown = useCallback(
    (edges: WorkspaceAgentPanelResizeEdges) => (event: ReactPointerEvent<HTMLElement>) => {
      event.stopPropagation();
      beginSession({ event, resize: edges });
    },
    [beginSession],
  );

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
      document.body.style.removeProperty('user-select');
    },
    [handlePointerMove, handlePointerUp],
  );

  return {
    isDragging,
    onHeaderPointerDown,
    onResizePointerDown,
    showSnapToRightGuide,
  };
};
